import { logError, logInfo } from 'src/loggingManager';
import { parseError } from 'src/util/misc';
import { createAsset, createVersion, updateAsset, getAsset, getVersion } from './assetsClient';
import { SceneType_NotSaved } from 'src/types/scene';
import { SceneService_AssetType_NotSaved } from 'src/types/asset';
import { CylinderWarpState } from './converters/cylinderWarp';
import { GarmentMarkersState } from './converters/garmentMarkers';
import { GarmentPlacementState } from './converters/garmentPlacement';
import { getSdk } from 'src/sdk';

type UpdateSceneParams = {
    tenantType: string;
    tenantId: string;
    sceneId: string;
    scene: Partial<SceneType_NotSaved>;
};

type InputSceneParams = Omit<UpdateSceneParams, 'sceneId'> & {
    scene: SceneType_NotSaved;
};

function mapSceneToAsset(scene: SceneType_NotSaved): Omit<SceneService_AssetType_NotSaved, 'tenant'> {
    const initialRevision = scene.initialVersion && {
        uri: scene.initialVersion.indexUri,
        metadata: {
            transformations: scene.initialVersion.transformations,
            markers: scene.initialVersion.markers,
            garmentPlacements: scene.initialVersion.garmentPlacements,
        },
    };
    return {
        name: scene.name,
        description: scene.description,
        notes: scene.notes,
        referenceId: scene.mcpSku,
        referenceIdVersion: scene.mcpSkuVersion ? +scene.mcpSkuVersion : 0,
        attributes: scene.attributes,
        assetType: 'scene',
        tags: scene.tags,
        latestRevisionId: scene.latestVersionId,
        publishedRevisionId: scene.publishedVersionId,
        deleted: !!scene.deleted,
        initialRevision,
    };
}

function mapPartialSceneToAsset(scene: Partial<SceneType_NotSaved>) {
    const initialRevision = scene.initialVersion && {
        uri: scene.initialVersion.indexUri,
        metadata: {
            transformations: scene.initialVersion.transformations,
            markers: scene.initialVersion.markers,
            garmentPlacements: scene.initialVersion.garmentPlacements,
        },
    };
    return {
        ...(scene.name !== undefined && { name: scene.name }),
        ...(scene.description !== undefined && { description: scene.description }),
        ...(scene.notes !== undefined && { notes: scene.notes }),
        ...(scene.mcpSku !== undefined && { referenceId: scene.mcpSku }),
        ...{ referenceIdVersion: scene.mcpSkuVersion !== undefined ? +scene.mcpSkuVersion : 0 },
        ...(scene.attributes !== undefined && { attributes: scene.attributes }),
        ...(scene.tags !== undefined && { tags: scene.tags }),
        ...(scene.latestVersionId !== undefined && { latestRevisionId: scene.latestVersionId }),
        ...(scene.publishedVersionId !== undefined && { publishedRevisionId: scene.publishedVersionId }),
        ...(scene.deleted !== undefined && { deleted: scene.deleted }),
        ...(initialRevision && { initialRevision }),
        assetType: 'scene',
    };
}

/**
 * Creates a scene.
 * @param options
 * @param options.tenantType - The type of owner resource (merchant / fulfiller).
 * @param options.tenantId - The id of the resource.
 * @param options.scene - The object containing all scene properties.
 * @param options.scene.name - Name of the scene.
 * @param options.scene.description - Description of the scene.
 * @param options.scene.notes - Notes of the scene.
 * @param options.scene.tags - Optional array of tags for the scene.
 * @param options.scene.mcpSku - Optional sku associated with the scene for searching.
 * @param options.scene.mcpSkuVersion - Optional sku version associated with the scene for searching.
 * @param options.scene.initialVersion - The optional initial version for the scene.
 * @param options.scene.initialVersion.indexUri - The link to the initial scene uri.
 * @param options.scene.initialVersion.transformations - Optional transformation data if this scene contains cylinders.
 * @param options.scene.initialVersion.markers - Optional garment marker calibration data if this scene contains apparel backgrounds.
 * @param options.scene.initialVersion.garmentPlacements - Optional GARMENT_WARP placement inputs for documents auto-placed on apparel.
 * @returns - The promise of the request.
 */
export function createScene({ tenantType, tenantId, scene }: InputSceneParams) {
    const asset = mapSceneToAsset(scene);
    asset.initialRevision = {
        uri: scene.initialVersion.indexUri,
        metadata: {
            transformations: scene.initialVersion.transformations,
            markers: scene.initialVersion.markers,
            garmentPlacements: scene.initialVersion.garmentPlacements,
        },
    };

    return createAsset({ asset, tenantType, tenantId }).then((newScene) => {
        logInfo(`Created scene: ${newScene} ${JSON.stringify(newScene || {})}`);
        return newScene;
    });
}

type CreateSceneVersionParams = {
    sceneId: string;
    version: {
        indexUri: string;
        transformations?: string | CylinderWarpState;
        markers?: string | GarmentMarkersState;
        garmentPlacements?: string | GarmentPlacementState;
    };
};

/** Creates a scene version.
 * @param options
 * @param options.sceneId - The id of the scene.
 * @param options.version - The scene version object.
 * @param options.version.indexUri - The link to the new scene uri.
 * @param options.version.transformations - Optional transformation data if this scene contains cylinders.
 * @param options.version.markers - Optional garment marker calibration data if this scene contains apparel backgrounds.
 * @param options.version.garmentPlacements - Optional GARMENT_WARP placement inputs for documents auto-placed on apparel.
 * @returns - The promise of the request.
 */
export function createSceneVersion({ sceneId, version }: CreateSceneVersionParams) {
    const mappedVersion = {
        uri: version.indexUri,
        metadata: {
            transformations: version.transformations,
            markers: version.markers,
            garmentPlacements: version.garmentPlacements,
        },
    };

    return createVersion({ assetId: sceneId, version: mappedVersion });
}

/**
 * Gets a scene.
 * @param options
 * @param options.sceneId - The id of the scene.
 * @returns - The promise of the request.
 */
export function getScene({ sceneId }: { sceneId: string }) {
    return getAsset({ assetId: sceneId }).then((asset) =>
        getVersion({ assetId: sceneId, versionId: asset.latestVersionId || '' }).then((version) => {
            if (Object.keys(version.metadata).length > 0 || Array.isArray(version.metadata)) {
                if (version.metadata.transformations) {
                    // eslint-disable-next-line no-param-reassign
                    asset.cylinderWarp = version.metadata.transformations;
                } else if (!version.metadata.markers && !version.metadata.garmentPlacements) {
                    // Legacy scenes predating the `{ transformations, markers, garmentPlacements }`
                    // wrapper stored the cylinder warp data as the metadata object itself, so this is
                    // the only way to recognize them. Once `markers` or `garmentPlacements` is
                    // present, we know this version was written by the current createSceneVersion
                    // (which always wraps cylinder data under `transformations`), so an apparel-only
                    // scene with no cylinders — just `{ markers: {...} }` and/or
                    // `{ garmentPlacements: {...} }` — must NOT be mistaken for legacy cylinder
                    // metadata (that previously made every markers-only scene fail to reload, since
                    // fromXml would try to read `.Cylinders` off the markers object and crash).
                    // eslint-disable-next-line no-param-reassign
                    asset.cylinderWarp = version.metadata;
                }
                if (version.metadata.markers) {
                    // eslint-disable-next-line no-param-reassign
                    asset.markers = version.metadata.markers;
                }
                if (version.metadata.garmentPlacements) {
                    // eslint-disable-next-line no-param-reassign
                    asset.garmentPlacements = version.metadata.garmentPlacements;
                }
            }
            return asset;
        }),
    );
}

/**
 * Updates a scene.
 * @param options
 * @param options.tenantType - The type of owner resource (merchant / fulfiller).
 * @param options.tenantId - The id of the resource.
 * @param options.sceneId - The id of the scene.
 * @param options.scene - The updated scene object.
 * @param options.scene.name - The updated scene name.
 * @param options.scene.mcpSku - The updated sku.
 * @param options.scene.description - The updated scene description.
 * @param options.scene.latestVersionId - The updated latest version.
 * @param options.scene.publishedVersionId - The updated published version.
 * @param options.scene.tags - string array of tags for the scene
 * @param options.scene.deleted - if the string is deleted or not
 * @returns - The promise of the request.
 */
export function updateScene({ tenantType, tenantId, sceneId, scene }: UpdateSceneParams) {
    const asset = mapPartialSceneToAsset(scene);
    return updateAsset({ tenantType, tenantId, assetId: sceneId, newAsset: asset });
}

/**
 * Clean scene texture (uv map) image
 * @param options
 * @param options.texture - texture image file
 * @returns The promise of the request.
 */
export function cleanTexture({ texture }: { texture: File }) {
    return getSdk()
        .Scenes.cleanTexture(texture)
        .then((cleanTexture) => new File([cleanTexture], texture.name, { type: 'image/png' }))
        .catch((e) => {
            logError(`Failure to clean texture image! ${parseError(e)}`);
            throw e;
        });
}

/**
 * get texture (uv map) image
 * @param options
 * @param options.width - width of identity texture image
 * @param options.height - height of identity texture image file
 * @returns The promise of the request.
 */
export function getTextureIdentity({ width, height }: { width: number; height: number }) {
    return getSdk()
        .Scenes.getTextureIdentity({ width, height })
        .then((identityTexture) => new File([identityTexture], 'identityTexture', { type: 'image/png' }))
        .catch((e) => {
            logError(`Failure to get identity texture image of ${width}x${height} size! ${parseError(e)}`);
            throw e;
        });
}
